import React, {
	useEffect,
	useState,
	useRef,
} from 'react';

import { getBearingPDFData } from '../../../components/bearing-search/api/api';

import { BearingPdfResponse } from '../../../components/bearing-search/types/types';

import Slider from 'react-slick';

type PartGalleryProps = {
	altText?: string;
	imageUrls: string[];
	pdfDetails?: string | undefined;
};

const PartGallery: React.FC<PartGalleryProps> = ({ imageUrls, altText, pdfDetails }) => {
	const [ mainSlider, setMainSlider ] = useState<Slider | null>(null);
	const [ thumbnailSlider, setThumbnailSlider ] = useState<Slider | null>(null);
	const thumbnailSliderRef = useRef<Slider | null>(null);
	const mainSliderRef = useRef<Slider | null>(null);
	const [ pdfDetailData, setPDFDetailData ] = useState<BearingPdfResponse | null>();
	const [ pdfLoading, setPdfLoading ] = useState<boolean>(true);
	const pdfDownloadLink = useRef('');

	const thumbnailSliderSettings = {
		slidesToShow: 3,
		infinite: false,
		swipeToSlide: true,
		focusOnSelect: true,
		asNavFor: mainSlider,
		className: 't-part-gallery__slider t-part-gallery__slider--thumbnails',
		responsive: [
			{
				breakpoint: 768,
				settings: {
					slidesToShow: 2,
				},
			},
		],
	};

	useEffect(() => {
		setMainSlider(mainSliderRef.current);
		setThumbnailSlider(thumbnailSliderRef.current);
		if (pdfDetails) {
			fetchBearingPdfFile();
		}
	}, []);

	const fetchBearingPdfFile = () => {
		getBearingPDFData(JSON.parse(pdfDetails || '').json).then((response) => {
			if (response.errors) {
				console.error('Error bearing PDF data:', response.message);
				setPdfLoading(false);
				return;
			}
			setPDFDetailData(response);
		}).catch((error) => {
			console.error('Error bearing PDF data:', error);
		});
	};

	const getPdfUrl = (pdfDownloadURL: string) => {
		if (! pdfDownloadURL) {
			return '';
		}
		const parts = pdfDownloadURL.split('?'),
			params = new URLSearchParams(parts[ 1 ]);
		return params.get('downloadurl');
	};

	const getPdfObjectUrl = (pdfBytes: string) => {
		if (! pdfBytes) {
			return '';
		}
		const arrayBuffer = base64ToArrayBuffer(pdfBytes),
			blob = new Blob([ arrayBuffer ], {
				type: 'application/pdf',
			});
		return window.URL.createObjectURL(blob);
	};

	const base64ToArrayBuffer = (base64: string) => {
		const binaryString = window.atob(base64);
		const binaryLen = binaryString.length;
		const bytes = new Uint8Array(binaryLen);
		for (let i = 0; i < binaryLen; i++) {
			bytes[ i ] = binaryString.charCodeAt(i);
		}
		return bytes;
	};

	const buildPdfUrls = () => {
		return {
			pdfObjectUrl: getPdfObjectUrl(pdfDetailData?.pdfBytes ?? ''),
			pdfUrl: getPdfUrl(pdfDetailData?.pdfDownloadURL ?? ''),
		};
	};
	const galleryClickHandler = (event) => {
		event.preventDefault();
		event.stopPropagation();
		const currentIndex = mainSlider?.innerSlider?.state.currentSlide;
		const currentSlide = mainSlider?.innerSlider?.list?.querySelectorAll('.slick-slide')[ currentIndex || 0 ];
		if (currentSlide?.querySelector('.t-part-gallery__slide__image--pdf')) {
			window.open(pdfDownloadLink.current, '_blank', 'noreferrer');
		}
	};
	const buildPdfElement = (isThumbnailGallery = false) => {
		if (! pdfDetailData) {
			return;
		}
		const { pdfObjectUrl, pdfUrl } = buildPdfUrls();
		pdfDownloadLink.current = pdfUrl || pdfObjectUrl;
		const pdfZoom = isThumbnailGallery ? 0.1 : 45;
		return (
			<iframe onLoad={() => {
				setPdfLoading(false);
			}} className='t-parts-details__gallery-embed-object-container t-part-gallery__slide__image t-part-gallery__slide__image--pdf' title='Bearing PDF' key={'gallery-pdf-slide-1'} src={`${ pdfUrl || pdfObjectUrl }#toolbar=0&zoom=${ pdfZoom }'`}></iframe>
		);
	};

	const buildSlideItems = (urls: string[], isThumbnailGallery = false): React.ReactElement[] => {
		if ((! urls || urls.length === 0) && ! pdfDetailData) {
			return [
				<h4 className='t-part-gallery--empty' key="gallery-empty">No images available.</h4>,
			];
		}
		let imageSlides: JSX.Element[] = [];
		if (urls && urls.length) {
			imageSlides = urls.map((url, index) => (
				<img
					key={`gallery-slide-${ index }`}
					src={url}
					alt={`Image ${ index + 1 } ${ altText !== '' ? `for ${ altText }` : '' }`}
					className="t-part-gallery__slide__image"
					onError={(event) => (event.target as HTMLImageElement).classList.add('hidden')}
				/>
			));
		}
		return [ ...imageSlides, ...[ buildPdfElement(isThumbnailGallery) ] ];
	};

	return (
		<div className={`t-part-gallery__container slider-container ${ pdfLoading ? 't-full-block-skeleton' : '' }`}>
			<a href="#" onClick={galleryClickHandler} target='_blank' rel='noreferrer'>
				<Slider
					arrows={false}
					asNavFor={thumbnailSlider}
					ref={mainSliderRef}
					infinite={false}
					swipeToSlide={false}
					draggable={false}
					className="t-part-gallery__slider t-part-gallery__slider--main">
					{buildSlideItems(imageUrls)}
				</Slider>
			</a>
			{(imageUrls.length > 1 || (imageUrls.length >= 1 && pdfDetailData)) && (
				<Slider
					{...thumbnailSliderSettings}
					ref={thumbnailSliderRef}
					className="t-part-gallery__slider t-part-gallery__slider--thumbnails"
				>
					{buildSlideItems(imageUrls, true)}
				</Slider>
			)}

		</div>
	);
};

export default PartGallery;
